import { useEffect, useState } from 'react'
import { InputAction, Options } from 'react-select'
import { useLookupDisplayValue } from '../../hooks/use-api'
import {
  AsyncSelectType,
  AUTOCOMPLETE_MIN_CHARS,
  ReadOnlyField,
  Typeahead,
  TypeaheadOption,
  TypeaheadOptionsProps,
} from '@msaf/core-react'

export interface LookupSearchProps extends Partial<Omit<TypeaheadOptionsProps, 'value' | 'setValue'>> {
  fieldId: string
  value: string
  setValue: (option: TypeaheadOption, isNew?: boolean) => void
  isClearable?: boolean
  isSkeleton?: boolean
  searchFunction?: (searchTerm: string, limit?: number) => Promise<Array<{ id: string; name: string }>>
  autoCompleteMinChars?: number
  selectType?: AsyncSelectType
  fieldFilters?: Record<string, string | number | boolean | null>
}

export default function LookupSearch({
  fieldId,
  value,
  setValue,
  labelledBy,
  isClearable,
  isSkeleton,
  searchFunction,
  autoCompleteMinChars = AUTOCOMPLETE_MIN_CHARS,
  isInvalid,
  menuPortalTarget,
  selectType,
  fieldFilters,
  isDisabled,
  ...passthroughProps
}: LookupSearchProps) {
  const [selectedOption, setSelectedOption] = useState<TypeaheadOption | null>(null)
  const [isNewlyCreatedOption, setIsNewlyCreatedOption] = useState(false)

  const {
    isLoading: isLoadingDisplayValue,
    isSuccess,
    data,
  } = useLookupDisplayValue(fieldId, value, isNewlyCreatedOption, fieldFilters)

  useEffect(() => {
    if (isSuccess && data?.id && data?.name) {
      const { id: value, name: label, location } = data
      setSelectedOption({ label, value, location })
    }
  }, [isSuccess, data, data?.id, data?.name])

  const handleChange = (option: TypeaheadOption & { __isNew__?: boolean }, _triggeredInputAction?: InputAction) => {
    setSelectedOption(option && option.value ? option : { label: '', value: '', location: '' })
    const { __isNew__: isNew, ...value } = option
    setIsNewlyCreatedOption(!!isNew)
    setValue(value, isNew)
  }

  const loadOptions = async (inputValue: string, callback: (options: Options<TypeaheadOption>) => void) => {
    if (inputValue == null || inputValue === '' || inputValue.length < autoCompleteMinChars) return callback([])
    if (inputValue.length >= autoCompleteMinChars && searchFunction) {
      const suggestions = await searchFunction(inputValue)
      if (suggestions) {
        const opts: TypeaheadOption[] = suggestions.map(({ id, name, ...rest }: { id: string; name: string }) => ({
          label: name,
          value: id,
          ...rest,
        }))
        callback(opts)
      }
    }
  }

  if (isDisabled) {
    return (
      <ReadOnlyField
        isSkeleton={isLoadingDisplayValue || isSkeleton}
        labelledBy={labelledBy ?? fieldId}
        value={selectedOption?.label}
      />
    )
  }

  return (
    <Typeahead
      {...passthroughProps}
      selectType={selectType ?? 'async'}
      labelledBy={labelledBy ?? fieldId}
      options={[]}
      loadOptions={loadOptions}
      handleChange={handleChange}
      selectedOption={selectedOption}
      isLoading={isSkeleton || isLoadingDisplayValue}
      isClearable={isClearable}
      isInvalid={isInvalid}
      menuPortalTarget={menuPortalTarget}
    />
  )
}
